import { ItemDefinition, Tree } from "../../../lib/components/tree/Tree";
import { ReviewItem, ReviewTreeNode } from "../utils/reviewModel";
import { useCallback, useMemo, useState } from "react";

import { LockBadge } from "../../locks/components/LockBadge";
import { LockMark } from "../../locks/models/LockDTO";
import { ReviewKindDot } from "./ReviewKindBadge";

/** What the update check found for a page, shown as a pill: conflicts left, all resolved, merges cleanly, changed by the last update. */
export type TreeStatus =
    | { kind: "conflicts"; count: number }
    | { kind: "resolved" }
    | { kind: "merges" }
    | { kind: "changed" };

interface IReviewTreeProps {
    tree: ReviewTreeNode[];
    selectedKey?: string;
    onSelect: (key: string) => void;
    lockOf: (item: ReviewItem) => LockMark | undefined;
    /** The page's update status; when absent, the item's own flags show (stale, failed, applied). */
    statusOf?: (item: ReviewItem) => TreeStatus | undefined;
}

/** The nodes whose title (or a descendant's) contains the search, ancestors kept so the matches stay in place. */
function filterTree(nodes: ReviewTreeNode[], search: string): ReviewTreeNode[] {
    if (!search.trim()) return nodes;
    const needle = search.trim().toLowerCase();
    return nodes.flatMap((node) => {
        const children = filterTree(node.children, search);
        return node.title.toLowerCase().includes(needle) || children.length ? [{ ...node, children }] : [];
    });
}

const flags: { when: (item: ReviewItem) => boolean; css: string; label: string; glyph: string }[] = [
    { when: (i) => i.stale, css: "stale", label: "Changed since the proposal", glyph: "!" },
    { when: (i) => i.outcome === "failed", css: "failed", label: "Failed", glyph: "✕" },
    { when: (i) => i.outcome === "applied", css: "applied", label: "Applied", glyph: "✓" },
    { when: (i) => i.outcome === "skipped", css: "skipped", label: "Not applied", glyph: "–" },
];

function Flags(props: { item: ReviewItem; status?: TreeStatus }) {
    const { status } = props;
    if (status?.kind === "conflicts") {
        const label = `${status.count} conflict${status.count === 1 ? "" : "s"} with edits made since`;
        return <span className="review-tree-flag conflict" role="img" aria-label={label} title={label}>{status.count}</span>;
    }
    if (status) {
        const text = status.kind === "resolved" ? "resolved" : status.kind === "merges" ? "merges" : "changed";
        const label = status.kind === "resolved" ? "Conflicts resolved" : status.kind === "merges" ? "Edits made since merge cleanly" : "Changed by the last update";
        const css = status.kind === "changed" ? "pill pill-warn" : "pill pill-ok";
        return <span className={`review-tree-flag ${css}`} role="img" aria-label={label} title={label}>{text}</span>;
    }
    return (
        <>
            {flags.filter((f) => f.when(props.item)).map((f) => (
                <span key={f.css} className={`review-tree-flag ${f.css}`} role="img" aria-label={f.label} title={f.label}>{f.glyph}</span>
            ))}
        </>
    );
}

/**
 * The changed pages where they sit in the space, on the app's page tree (as in Documentation); unchanged ancestors are shown dimmed
 * for context and every changed page carries its kind as a coloured dot.
 */
export function ReviewTree(props: IReviewTreeProps) {
    const { onSelect, lockOf, selectedKey, statusOf } = props;
    const [search, setSearch] = useState("");
    const items = useMemo(() => filterTree(props.tree, search), [props.tree, search]);

    const itemDefinition: ItemDefinition<ReviewTreeNode> = useMemo(() => ({
        getId: (node) => node.key,
        children: (node) => node.children,
        render: (node) =>
            node.item ? (
                <span className={`review-tree-label${node.item.kind === "deleted" ? " deleted" : ""}`}>
                    <span className="review-tree-title" title={node.item.title}>{node.item.title}</span>
                    <LockBadge mark={lockOf(node.item)} />
                    <Flags item={node.item} status={statusOf?.(node.item)} />
                    <ReviewKindDot kind={node.item.kind} />
                </span>
            ) : (
                <span className="review-tree-label review-tree-context" title={`${node.title} (unchanged)`}>{node.title}</span>
            ),
        onItemClicked: (node) => node.item && onSelect(node.item.key),
    }), [lockOf, onSelect, statusOf]);

    // Tree keeps the selected node by identity and re-reads it when this callback changes: rebuilt nodes need a new one too
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const isItemActive = useCallback((node: ReviewTreeNode) => node.key === selectedKey, [selectedKey, items]);

    return (
        <nav className="review-tree" aria-label="Changed pages">
            <Tree
                items={items}
                itemDefinition={itemDefinition}
                header={{ label: "Changed pages" }}
                isItemActive={isItemActive}
                searchValue={search}
                filter={setSearch}
                defaultExpanded
            />
        </nav>
    );
}
